import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { SkeletonLine } from 'components/Skeleton'
import { RAG } from 'features/dashboard/charts/scoreColour'
import InfoPopover, { PopLead } from 'features/actions/workspace/InfoPopover'
import { formatDay } from 'features/actions/workspace/remedialDates'
import { severityMeta } from 'features/actions/workspace/remedialMeta'

const STEP = 10

const EXPLAIN = 'These are remedials nobody has decided on yet, shown on the day they are due. Once a task is created for one, its deadline shows as a task instead, so nothing appears twice.'

const RemedialsDueSection = ({ heading, items, total, loading, failed, search, compact = false, onOpen, onSeeAll }) => {
  const [visible, setVisible] = useState(STEP)
  const list = useMemo(() => items || [], [items])
  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase()
    if (!q) return list
    return list.filter(item => `${item.title} ${item.buildingPassport?.name || ''}`.toLowerCase().includes(q))
  }, [list, search])

  useEffect(() => { setVisible(STEP) }, [list, search])

  const shown = filtered.slice(0, visible)
  const firstPaint = loading && list.length === 0
  const capped = !search && total > list.length

  return (
    <Section aria-label={heading} $compact={compact}>
      {compact ? (
        <Intro>
          <span>Remedials still waiting for a decision.</span>
          <InfoPopover label='Which remedials show here?' title='Remedials on the calendar'>
            <PopLead>{EXPLAIN}</PopLead>
          </InfoPopover>
        </Intro>
      ) : (
        <Head>
          <HeadIcon aria-hidden='true'><BoltOutlinedIcon /></HeadIcon>
          <Title>{heading}</Title>
          <InfoPopover label='Which remedials show here?' title='Remedials on the calendar'>
            <PopLead>{EXPLAIN}</PopLead>
          </InfoPopover>
          {!firstPaint && !failed && <Count>{total}</Count>}
        </Head>
      )}
      {firstPaint && [0, 1, 2].map(i => (
        <SkelRow key={i} aria-hidden='true'>
          <SkelTile />
          <SkelLines>
            <SkeletonLine style={{ width: '70%' }} />
            <SkeletonLine style={{ width: '40%' }} />
          </SkelLines>
        </SkelRow>
      ))}
      {failed && !loading && (
        <Empty role='alert'>We could not load remedials just now. Refresh the page to try again.</Empty>
      )}
      {!firstPaint && !failed && filtered.length === 0 && (
        <Empty>{search ? 'No remedials match your search.' : 'No remedials are due without a task.'}</Empty>
      )}
      {shown.length > 0 && (
        <Rows>
          {shown.map(item => {
            const sev = severityMeta(item.severity)
            return (
              <Row key={item.id} type='button' onClick={() => onOpen(item.id)} aria-label={`${sev.label} remedial: ${item.title}. Open it`}>
                <Tile style={{ color: sev.color, background: sev.bg }} aria-hidden='true'><BoltOutlinedIcon /></Tile>
                <Main>
                  <RowTitle title={item.title}>{item.title}</RowTitle>
                  <Meta>
                    <DueDate $overdue={item.isOverdue}>Due {formatDay(item.dueDate)}</DueDate>
                    {item.isOverdue && <Badge style={{ color: RAG.critical.fg, background: RAG.critical.bg }}>Overdue</Badge>}
                    <Badge style={{ color: sev.color, background: sev.bg }}>{sev.label}</Badge>
                    {item.buildingPassport?.name && <Building>{item.buildingPassport.name}</Building>}
                  </Meta>
                </Main>
              </Row>
            )
          })}
        </Rows>
      )}
      <InfiniteScrollSentinel hasMore={visible < filtered.length} loading={false} onLoadMore={() => setVisible(v => v + STEP)} />
      {!firstPaint && filtered.length > 0 && (
        <Foot>
          <span>Showing {shown.length} of {search ? filtered.length : total}</span>
          {capped && <SeeAll type='button' onClick={onSeeAll}>See all of them in Remedials</SeeAll>}
        </Foot>
      )}
    </Section>
  )
}

export default RemedialsDueSection

const Section = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid #f2f4f7;

  @media (max-width: 640px) { padding: 14px; }
`

const HeadIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f2f4f7;
  color: #344054;
  svg { font-size: 18px; }
`

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0C1229;
`

const Count = styled.span`
  margin-left: auto;
  min-width: 28px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #f2f4f7;
  font-size: 13px;
  font-weight: 700;
  color: #344054;
  text-align: center;
`

const Intro = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13.5px;
  color: #475467;

  @media (max-width: 640px) { padding: 10px 14px; }
`

const SkelRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid #f2f4f7;
`

const SkelTile = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f2f4f7;
  flex-shrink: 0;
`

const SkelLines = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`

const Empty = styled.p`
  margin: 0;
  padding: 20px;
  font-size: 15px;
  color: #667085;
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  min-height: 56px;
  padding: 12px 20px;
  border: none;
  border-bottom: 1px solid #f2f4f7;
  background: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #f8fafc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: -2px; }

  @media (max-width: 640px) { padding: 12px 14px; gap: 10px; }
`

const Tile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  svg { font-size: 18px; }
`

const Main = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const RowTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #0C1229;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Meta = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
`

const DueDate = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $overdue }) => ($overdue ? RAG.critical.fg : '#0C1229')};
`

const Badge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
`

const Building = styled.span`
  font-size: 12px;
  color: #667085;
`

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 20px;
  font-size: 13px;
  color: #475467;

  @media (max-width: 640px) { padding: 12px 14px; }
`

const SeeAll = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid #33becc;
  border-radius: 22px;
  background: #fff;
  color: #0C1229;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #e6f7f9; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`
